// Statewide base sales tax rates, used ONLY as a starting suggestion on a client quote. The owner always
// types or confirms the rate; Canopy never presents this as tax advice.
//
// SOURCE: Tax Foundation, "2026 Sales Tax Rates, Midyear Update" (rates as of July 1, 2026):
//   https://taxfoundation.org/data/all/state/2026-sales-tax-rates-midyear/
// Checked against this table on 2026-10-27. Re-check every January and July, and against each state's department
// of revenue before relying on it.
// DEFINITION used for every state: the lowest rate charged anywhere in the state. That is the state rate plus any
// local portion that is mandatory statewide (for example Utah and Virginia). Many cities and counties add more,
// and whether a job's materials, labor or services are taxable at all differs by state. Hawaii, New Mexico and
// Arizona use a gross receipts / excise style tax that is not a sales tax in the strict sense.
//
// rate is a percent as text so it can be used with the exact percent math in clientQuote.js ("6.25" = 6.25%).
// A state with no statewide sales tax has rate "0" and noStateTax: true.

export const SALES_TAX_AS_OF = "2026-07-01";

const S = (code, name, rate, note) => ({ code, name, rate, noStateTax: rate === "0", note: note || "" });

export const STATE_SALES_TAX = [
  S("AL", "Alabama", "4"),
  S("AK", "Alaska", "0", "Alaska has no statewide sales tax, but many local governments charge their own."),
  S("AZ", "Arizona", "5.6", "Arizona calls this a transaction privilege tax."),
  S("AR", "Arkansas", "6.5"),
  S("CA", "California", "7.25"),
  S("CO", "Colorado", "2.9", "Many Colorado cities and counties add their own tax."),
  S("CT", "Connecticut", "6.35"),
  S("DE", "Delaware", "0", "Delaware has no sales tax."),
  S("DC", "District of Columbia", "6", "DC is scheduled to rise to 7% on Oct 1, 2027."),
  S("FL", "Florida", "6"),
  S("GA", "Georgia", "4"),
  S("HI", "Hawaii", "4", "Hawaii charges a general excise tax, not a sales tax."),
  S("ID", "Idaho", "6"),
  S("IL", "Illinois", "6.25"),
  S("IN", "Indiana", "7"),
  S("IA", "Iowa", "6"),
  S("KS", "Kansas", "6.5"),
  S("KY", "Kentucky", "6"),
  S("LA", "Louisiana", "5"),
  S("ME", "Maine", "5.5"),
  S("MD", "Maryland", "6"),
  S("MA", "Massachusetts", "6.25"),
  S("MI", "Michigan", "6"),
  S("MN", "Minnesota", "6.875"),
  S("MS", "Mississippi", "7"),
  S("MO", "Missouri", "4.225"),
  S("MT", "Montana", "0", "Montana has no statewide sales tax."),
  S("NE", "Nebraska", "5.5"),
  S("NV", "Nevada", "6.85"),
  S("NH", "New Hampshire", "0", "New Hampshire has no sales tax."),
  S("NJ", "New Jersey", "6.625"),
  S("NM", "New Mexico", "4.875", "New Mexico charges a gross receipts tax, not a sales tax."),
  S("NY", "New York", "4"),
  S("NC", "North Carolina", "4.75"),
  S("ND", "North Dakota", "5"),
  S("OH", "Ohio", "5.75"),
  S("OK", "Oklahoma", "4.5"),
  S("OR", "Oregon", "0", "Oregon has no sales tax."),
  S("PA", "Pennsylvania", "6"),
  S("RI", "Rhode Island", "7"),
  S("SC", "South Carolina", "6"),
  S("SD", "South Dakota", "4.2"),
  S("TN", "Tennessee", "7"),
  S("TX", "Texas", "6.25"),
  S("UT", "Utah", "6.1"),
  S("VT", "Vermont", "6"),
  S("VA", "Virginia", "5.3"),
  S("WA", "Washington", "6.5"),
  S("WV", "West Virginia", "6"),
  S("WI", "Wisconsin", "5"),
  S("WY", "Wyoming", "4"),
];

export function findState(code) {
  return STATE_SALES_TAX.find((s) => s.code === code) || null;
}

// The suggestion shown under the state picker. Returns null for no state / an unknown code.
// { rate, text, noTax }: text is the plain sentence. noTax is true for a state with no statewide sales tax; the
// screen then offers "Turn sales tax off" instead of "Use this rate".
export function taxSuggestion(code) {
  const s = findState(code);
  if (!s) return null;
  if (s.noStateTax) {
    return { rate: "0", noTax: true, text: `No statewide sales tax in ${s.name}. If you don't charge sales tax, turn it off.` };
  }
  return {
    rate: s.rate,
    noTax: false,
    text: `Suggested: ${s.rate}% for ${s.name}, the lowest rate charged anywhere in the state. Many cities and counties add more; check your state's rules.${s.note ? " " + s.note : ""}`,
  };
}
